import type { Schemas } from '#shopware';
import { useCartFunction as swUseCart } from '#imports';
import { createSharedComposable } from '@vueuse/core';

type UseCartReturnExtended = UseCartReturn & {
  taxesTotal: ComputedRef<number>;
  productDiscount: ComputedRef<number>;
  totalPromotionSum: ComputedRef<number>;
  totalProductDiscount: ComputedRef<number>;
  isProductInAppliedPromotions: (productId: string) => boolean;
};

const _useCart = (): UseCartReturnExtended => {
  const useCartData: UseCartReturn = swUseCart();

  /**
   * Example on how to override the default `refreshCart` method
   *
   * @param {Schemas["Cart"]} newCart
   * @returns
   */
  async function refreshCart(
    newCart?: Schemas['Cart']
  ): Promise<Schemas['Cart']> {
    return useCartData.refreshCart(newCart);
  }

  const taxesTotal = computed(() => {
    const tax = useCartData?.cart.value?.price?.calculatedTaxes[0]?.tax;
    return tax || 0;
  });

  const isProductInAppliedPromotions = (productId: string) => {
    return useCartData.appliedPromotionCodes.value?.some((promotion) =>
      Object.values(promotion.payload?.composition || {}).some(
        (item) => item.id === productId && Boolean(item?.discount)
      )
    );
  };

  const productDiscount = computed(() => {
    const discountSum = useCartData.cartItems.value
      .filter(
        (item) =>
          item.type === 'product' && !isProductInAppliedPromotions(item.id)
      )
      .reduce(
        (accumulator, { price, quantity }: Schemas['LineItem']) =>
          accumulator + Number((price?.listPrice?.discount || 0) * quantity),
        0
      );

    return discountSum;
  });

  const totalPromotionSum = computed(() => {
    let totalDiscount = 0;

    useCartData.appliedPromotionCodes.value?.forEach((promotion) => {
      if (promotion.payload?.composition) {
        Object.values(promotion.payload.composition).forEach((item) => {
          totalDiscount += item.discount;
        });
      }
    });

    return totalDiscount;
  });

  const totalProductDiscount = computed(() => {
    return (
      -Math.abs(productDiscount.value) + -Math.abs(totalPromotionSum.value)
    );
  });

  return {
    ...useCartData,
    refreshCart,
    taxesTotal,
    productDiscount,
    totalPromotionSum,
    totalProductDiscount,
    isProductInAppliedPromotions,
  };
};

export const useCart = createSharedComposable(_useCart);
